import {
  ArrayMaxSize,
  IsArray,
  IsIn,
  IsInt,
  IsISO8601,
  IsObject,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  MinLength,
} from 'class-validator';

/**
 * Officer teamwork: notes with @mentions, following a file, shift handovers,
 * the private calendar feed and the work-summary digest.
 *
 * Shared by the Government Portal web app, the mobile app and gov-api. Nested
 * objects are validated server-side rather than with `@ValidateNested`, which
 * needs class-transformer metadata this browser-loaded lib does not carry.
 */

export const NOTE_BODY_MAX = 4000;
export const MAX_MENTIONS = 20;
export const HANDOVER_NOTE_MAX = 4000;
export const HANDOVER_FILE_NOTE_MAX = 500;
export const MAX_HANDOVER_FILES = 50;

/** Someone who could be mentioned, or handed a shift. Only people who can read the file. */
export interface TeamworkPersonDto {
  userId: string;
  displayName: string;
  /** Their seats, as the access model names them, for disambiguation. */
  roles: string[];
}

export interface MentionRefDto {
  userId: string;
  displayName: string;
}

// --- Notes ----------------------------------------------------------------

export interface FileNoteDto {
  id: string;
  fileId: string;
  author: { id: string | null; name: string };
  /** Empty once deleted; check `deletedAt`. */
  body: string;
  mentions: MentionRefDto[];
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  /** The caller wrote it — drives the edit and delete affordances. */
  mine: boolean;
}

export class CreateFileNoteDto {
  @IsString()
  @MinLength(1)
  @MaxLength(NOTE_BODY_MAX)
  body!: string;

  /**
   * Who the note mentions. The body carries the readable "@Name"; these carry
   * identity. Anyone who cannot read the file is dropped, never notified.
   */
  @IsOptional()
  @IsArray()
  @ArrayMaxSize(MAX_MENTIONS)
  @IsUUID('all', { each: true })
  mentionUserIds?: string[];
}

export class UpdateFileNoteDto extends CreateFileNoteDto {}

// --- Watching -------------------------------------------------------------

export interface FileWatchStateDto {
  watching: boolean;
  watcherCount: number;
}

export interface WatchedFileDto {
  fileId: string;
  reference: string;
  address: string;
  stage: string;
  status: string;
  watchedAt: string;
  /** The most recent recorded change on the file, if any. */
  lastActivity: {
    action: string;
    actorName: string | null;
    at: string;
  } | null;
}

// --- Handovers ------------------------------------------------------------

export type HandoverDirection = 'sent' | 'received' | 'other';

export interface ShiftHandoverFileDto {
  fileId: string;
  reference: string;
  address: string;
  note: string | null;
}

export interface ShiftHandoverDto {
  id: string;
  localGovernmentId: string;
  from: { id: string; name: string };
  to: { id: string; name: string };
  periodStart: string;
  periodEnd: string;
  note: string;
  files: ShiftHandoverFileDto[];
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgementNote: string | null;
  cancelledAt: string | null;
  /** The caller's side of it. `other` is a reader of a file it covers. */
  direction: HandoverDirection;
}

export interface ShiftHandoverListDto {
  received: ShiftHandoverDto[];
  sent: ShiftHandoverDto[];
  /** Received and not yet acknowledged — the badge. */
  pending: number;
}

export class CreateShiftHandoverDto {
  @IsUUID()
  toUserId!: string;

  @IsISO8601()
  periodStart!: string;

  @IsISO8601()
  periodEnd!: string;

  @IsString()
  @MinLength(1)
  @MaxLength(HANDOVER_NOTE_MAX)
  note!: string;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(MAX_HANDOVER_FILES)
  @IsUUID('all', { each: true })
  fileIds?: string[];

  /** A line per file, keyed by file id. Checked server-side. */
  @IsOptional()
  @IsObject()
  fileNotes?: Record<string, string>;

  /** Required when there are no files: the municipality the handover is for. */
  @IsOptional()
  @IsUUID()
  localGovernmentId?: string;
}

export class AcknowledgeShiftHandoverDto {
  @IsOptional()
  @IsString()
  @MaxLength(1000)
  note?: string;
}

// --- Calendar feed --------------------------------------------------------

export interface CalendarFeedStatusDto {
  active: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
}

/** Shown once: the token is stored hashed and cannot be read back. */
export interface CalendarFeedCreatedDto extends CalendarFeedStatusDto {
  url: string;
  /** The same link with the `webcal:` scheme, which opens a subscribe prompt. */
  webcalUrl: string;
}

// --- Digest preferences ---------------------------------------------------

export const DIGEST_FREQUENCY_VALUES = ['off', 'daily', 'weekly'] as const;
export type DigestFrequency = (typeof DIGEST_FREQUENCY_VALUES)[number];

export const DIGEST_LANGUAGE_VALUES = ['account', 'en', 'fr'] as const;
export type DigestLanguage = (typeof DIGEST_LANGUAGE_VALUES)[number];

export interface TeamworkPreferencesDto {
  digestFrequency: DigestFrequency;
  /** Local hour, 0–23. */
  digestHour: number;
  /** ISO weekday for the weekly digest: 1 = Monday … 7 = Sunday. */
  digestWeekday: number;
  digestLanguage: DigestLanguage;
  timezone: string;
  lastDigestSentAt: string | null;
  /**
   * Whether any municipality the officer works at has switched teamwork email
   * and push on. When false, the settings screen says so: the switches still
   * save, but nothing will leave the portal until it is.
   */
  outboundEnabled: boolean;
}

export class UpdateTeamworkPreferencesDto {
  @IsOptional()
  @IsIn(DIGEST_FREQUENCY_VALUES)
  digestFrequency?: DigestFrequency;

  @IsOptional()
  @IsInt()
  @Min(0)
  @Max(23)
  digestHour?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(7)
  digestWeekday?: number;

  @IsOptional()
  @IsIn(DIGEST_LANGUAGE_VALUES)
  digestLanguage?: DigestLanguage;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  timezone?: string;
}

/**
 * The visible "@Name" for a mention, as the composers insert it. Kept here so
 * web and mobile write it identically and highlight it the same way.
 */
export function mentionToken(displayName: string): string {
  return `@${displayName}`;
}

/**
 * The `@query` being typed at the caret, if any: the text after the last `@`
 * that starts a word, up to the caret, with no line break in it. Returns null
 * when the caret is not inside a mention.
 */
export function activeMentionQuery(text: string, caret: number): string | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1] ?? '')) return null;
  const query = before.slice(at + 1);
  if (/[\n\r]/.test(query) || query.length > 40) return null;
  return query;
}

/**
 * Replace the `@query` at the caret with the chosen person's token, returning
 * the new text and caret. A trailing space is added so typing continues.
 */
export function applyMention(
  text: string,
  caret: number,
  displayName: string,
): { text: string; caret: number } {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return { text, caret };
  const insert = `${mentionToken(displayName)} `;
  const next = `${text.slice(0, at)}${insert}${text.slice(caret)}`;
  return { text: next, caret: at + insert.length };
}

/** Keep only mention ids whose "@Name" still appears in the text. */
export function mentionsStillPresent(
  text: string,
  chosen: readonly MentionRefDto[],
): MentionRefDto[] {
  const seen = new Set<string>();
  return chosen.filter((m) => {
    if (seen.has(m.userId) || !text.includes(mentionToken(m.displayName))) return false;
    seen.add(m.userId);
    return true;
  });
}

/** Split text into plain and mention segments, for highlighting. */
export function splitMentions(
  text: string,
  mentions: readonly MentionRefDto[],
): Array<{ kind: 'text'; text: string } | { kind: 'mention'; text: string; userId: string }> {
  const tokens = [...mentions]
    .filter((m) => m.displayName)
    .sort((a, b) => b.displayName.length - a.displayName.length);
  const out: Array<
    { kind: 'text'; text: string } | { kind: 'mention'; text: string; userId: string }
  > = [];
  let i = 0;
  let buffer = '';
  while (i < text.length) {
    const match =
      text[i] === '@'
        ? tokens.find((m) => text.startsWith(mentionToken(m.displayName), i))
        : undefined;
    if (match) {
      if (buffer) out.push({ kind: 'text', text: buffer });
      buffer = '';
      const token = mentionToken(match.displayName);
      out.push({ kind: 'mention', text: token, userId: match.userId });
      i += token.length;
    } else {
      buffer += text[i];
      i += 1;
    }
  }
  if (buffer) out.push({ kind: 'text', text: buffer });
  return out;
}
